import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import {environment} from 'src/environments/environment';
import {ViewportScroller} from "@angular/common";
import {ApplicationLogDetailsRequest, ApplicationLogStepRequest, ApplicationLogsRequest, Log, Step} from "leia-api";
import NameEnum = Step.NameEnum;
import StateEnum = Step.StateEnum;
import OnboardingSourceEnum = ApplicationLogDetailsRequest.OnboardingSourceEnum;
import OnboardingStateEnum = ApplicationLogStepRequest.OnboardingStateEnum;
import OnboardingStepEnum = ApplicationLogStepRequest.OnboardingStepEnum;

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  static deviceRiskSessionId : string = "";
  static canRouteInternal: boolean = false;

  constructor(
    private logger: NGXLogger,
    private viewportScroller: ViewportScroller
  ) { }

  /**
   * Replace n number of values in a given string.
   * @param inString String needing replacement values.
   * @param replaceVals Key-value pairs of replacement strings and their values.
   */
  replaceStringContent(inString: string, replaceVals: Map<string, string>): string {
    let outString: string = inString;

    if (environment.development){
        this.logger.debug("**********************\n");
        this.logger.debug("replacement string = " + inString);
    }

    replaceVals.forEach((value: string, key: string) => {
        var escapedFind = key.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
        outString = outString.replace(new RegExp(escapedFind, 'gi'), value);
        if (environment.development){
          this.logger.debug("key = " + key);
          this.logger.debug("value = " + value);
          this.logger.debug("outString = " + outString);
        }
    });

    return outString;
  }

  /**
   * Utility method for cloning an object. Refactored out of signup.service.ts in PR !13437.
   * @param inObject The object to be cloned.
   */
  cloneObject(inObject: any): any {
    return Object.assign({}, inObject);
  }

  /**
   * Utility method for creating an application Log.
   * @param source
   * @param details
   */
  createLog(source: string, details: string): Log {
    let log = new Log();
    log.source = source;
    log.timestamp = new Date().toISOString();
    log.details = details;
    return log;
  }

  /**
   * Utility method for creating an application Log.
   * @param source
   * @param details
   */
  createStep(name: NameEnum, state: StateEnum, logs: Log[]): Step {
    let step = new Step();

    step.name = name;
    step.state = state;
    step.timestamp = new Date().toISOString();
    step.logs = logs;

    return step;
  }

  createApplicantStep(name: NameEnum, applicantId : number, state: StateEnum, logs: Log[]): Step {
    let step = this.createStep(name, state, logs);
    step.applicantId = applicantId;

    return step;
  }

  /**
   * Utility method for creating an application Log.
   * @param source
   * @param details
   */
  createEmptyStepForLogging(logs: Log[]): Step {
    let step = new Step();

    step.timestamp = new Date().toISOString();
    step.logs = logs;

    return step;
  }

  scrollToElement(elementId: string): void {
    document.getElementById(elementId)?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  }

  createApplicationLogDetailsRequest(source : OnboardingSourceEnum, details: string) : ApplicationLogDetailsRequest {
    let applicationLogDetailsRequest = new ApplicationLogDetailsRequest();

    applicationLogDetailsRequest.onboardingSource = source;
    applicationLogDetailsRequest.details = details;

    return applicationLogDetailsRequest;
  }
  createApplicationLogStepRequest(step : OnboardingStepEnum, state : OnboardingStateEnum, source : OnboardingSourceEnum, logs : ApplicationLogStepRequest[]) : ApplicationLogStepRequest {
    let applicationLogStepRequest = new ApplicationLogStepRequest();

    applicationLogStepRequest.onboardingStep = step;
    applicationLogStepRequest.onboardingSource = source;
    applicationLogStepRequest.onboardingState = state;
    applicationLogStepRequest.applicationLogDetailsRequestList = logs;

    return applicationLogStepRequest;
  }

  createEntireApplicationLogStepRequest(step : OnboardingStepEnum, state : OnboardingStateEnum, source : OnboardingSourceEnum, applicantId : number, rcCode : string | undefined, logs : ApplicationLogStepRequest[]) : ApplicationLogStepRequest {
    let applicationLogStepRequest = new ApplicationLogStepRequest();

    applicationLogStepRequest.onboardingStep = step;
    applicationLogStepRequest.onboardingSource = source;
    applicationLogStepRequest.onboardingState = state;
    applicationLogStepRequest.applicantId = applicantId;
    applicationLogStepRequest.applicationLogDetailsRequestList = logs;

    if(rcCode !== undefined && rcCode !== null) applicationLogStepRequest.rcCode = rcCode;

    return applicationLogStepRequest;
  }

  static sanitizeApplicantsDateOfBirth(applicationLogsRequest: ApplicationLogsRequest): void {
    applicationLogsRequest.applicants?.forEach(applicant => applicant.dateOfBirth = UtilitiesService.removeTimeFromDateString(applicant.dateOfBirth));
  }

  static removeTimeFromDateString(date: string | undefined): string {
    if (!date) {
      return "";
    }

    if (date.length >= 10) {
      return date.slice(0, 10);
    }

    return "";
  }

  static addTimeToDateString(date: string | undefined): string {
    if (!date) {
      return "";
    }

    if (date.length >= 10) {
      return `${date.slice(0, 10)} 12:00`;
    }

    return "";
  }

  static getDateString(date: Date | string): string {
    if (!date) { return ""; }

    let dateObject;
    if (typeof date === "string") {
      if (date === "") return "";

      dateObject = new Date(UtilitiesService.addTimeToDateString(date));
    } else {
      dateObject = date as Date;
    }

    const d = dateObject.getDate().toString();
    const day = d.length === 1 ? `0${d}` : d;

    const m = (dateObject.getMonth() + 1).toString();
    const month = m.length === 1 ? `0${m}` : m;

    const year = dateObject.getFullYear().toString();

    return `${year}-${month}-${day}`;
  }
  
  static getLastNCharacters(str: string | undefined, n: number): string {
    if (!str) { return ""; }

    return str.slice(-n);
  }
}

export class ValidArray<T> {
  private _array: Array<T> = [];

  constructor(array: Array<T> | null | undefined) {
    if (Array.isArray(array)) {
      this._array = array;
    }
  }

  first(): T | undefined{
    if (this._array[0]) {
      return this._array[0]
    }

    return undefined;
  }

  hasValues(): boolean {
    return this._array.length > 0;
  }

  value(): Array<any> {
    return this._array;
  }
}
